import type { ContactSalesChartColumn } from './contactSalesData';
import { numberFormat } from 'Components/Filters/Currencies';
import BemClass from '@upsales/components/Utils/bemClass';
import React, { useEffect, useMemo, useRef } from 'react';
import { Loader } from '@upsales/components';
import T from 'Components/Helpers/translate';
import Highcharts from 'highcharts';

import './ContactSalesChart.scss';

export type ContactSalesSeries = 'sales' | 'contributionMargin';

/**
 * Angular's two series colours (contactOrder.js `buildChart`): order value in green, contribution
 * margin in the darker green. Both are still live hex values elsewhere in the app, so there is no
 * palette variable to swap them for.
 */
const SERIES_COLOR: Record<ContactSalesSeries, string> = {
	sales: '#5cb85c',
	contributionMargin: '#2B7B2B'
};

const SERIES_NAME: Record<ContactSalesSeries, string> = {
	sales: 'default.order',
	contributionMargin: 'default.contributionMargin'
};

/** Angular's `compact` breakpoint - below it the x-axis falls back to `'26` / initials. */
const isCompact = () => document.body.clientWidth <= 1180;

export const buildChartOptions = (
	columns: ContactSalesChartColumn[],
	series: ContactSalesSeries
): Highcharts.Options => {
	const compactLabels = new Map(columns.map(column => [column.label, column.compactLabel]));

	return {
		chart: {
			type: 'column',
			height: 166,
			backgroundColor: 'transparent',
			style: { fontFamily: '"Roboto"' },
			marginBottom: 35
		},
		colors: [SERIES_COLOR[series]],
		title: { text: '' },
		subtitle: { text: undefined },
		credits: { enabled: false },
		legend: { enabled: false },
		xAxis: {
			lineColor: 'transparent',
			tickLength: 0,
			categories: columns.map(column => column.label),
			labels: {
				rotation: 0,
				style: { fontSize: '10px' },
				formatter: function (this: any) {
					return isCompact() ? compactLabels.get(this.value) ?? this.value : this.value;
				}
			}
		},
		yAxis: {
			min: 0,
			title: { text: '' },
			gridLineColor: '#e0e0e0',
			endOnTick: true,
			maxPadding: 0.02,
			labels: {
				// Plain thousands-separated numbers - `10 000`, `20 000`. This is the very filter
				// Angular's y-axis used (`$filter('numberFormat')`), so no `SEK` suffix and no `md`
				// abbreviation, both of which came from the Reportcenter widget's own formatter.
				formatter: function (this: any) {
					return this.value > 0 ? numberFormat(this.value) : '';
				}
			}
		},
		tooltip: { enabled: true },
		plotOptions: {
			column: {
				pointPadding: 0,
				borderWidth: 0,
				borderRadius: 4,
				// Angular pinned `pointWidth: 70`, which forces bars wider than their slot as soon
				// as the card is narrower than twelve times that. A max lets a lone bar look like
				// Angular's while twelve of them still fit without a horizontal scrollbar.
				maxPointWidth: 70
			}
		},
		series: [
			{
				type: 'column',
				name: T(SERIES_NAME[series]),
				data: columns.map(column => (series === 'sales' ? column.value : column.contributionMargin))
			}
		]
	};
};

type Props = {
	columns: ContactSalesChartColumn[];
	series: ContactSalesSeries;
	loading: boolean;
};

/**
 * The contact card's sales chart.
 *
 * Bars over an axis of periods (or users/stages) and nothing else - the same shape the Angular
 * contact card drew through `up-account-chart-sales`, and rendered the same way: straight
 * Highcharts over data this card fetched itself.
 */
const ContactSalesChart = ({ columns, series, loading }: Props) => {
	const classes = new BemClass('ContactSalesChart');
	const chartElement = useRef<HTMLDivElement>(null);

	const options = useMemo(() => buildChartOptions(columns, series), [columns, series]);

	useEffect(() => {
		if (!chartElement.current) {
			return;
		}
		const chart = Highcharts.chart(chartElement.current, options);
		return () => chart?.destroy();
	}, [options]);

	return (
		<div className={classes.b()}>
			{/* The chart container stays mounted while loading: Highcharts renders into this very
			    node, and hiding it keeps the previous grouping's bars from reading as the new one's. */}
			<div ref={chartElement} className={classes.elem('chart').mod({ loading }).b()} />
			{loading ? <Loader className={classes.elem('loader').b()} size="sm" /> : null}
		</div>
	);
};

export default ContactSalesChart;
